import ArrowDownBottomLine from '@assets/icons/arrow-down-bottom-line.svg';
import ClockIcon from '@assets/icons/clock.svg';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Platform, ScrollView, View } from 'react-native';
import { AvailabilityConstraints } from '@/experiences/sales-portal/domain/ordering/blocs/loaders/use-sales-portal-available-order-fulfillments-loader';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { ChangeFulfillmentTrigger } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/change-fulfillment-trigger';
import { CheckoutPanelContent } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/checkout-panel-content';
import { CheckoutPanelOrderPlaced } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/checkout-panel-order-placed';
import { CheckoutPanelTotalSummary } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/checkout-panel-total-summary';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeRole, GetAvailableOrderFulfillmentResponse } from '@/types';
import { FulfillmentDropdown } from '@/experiences/sales-portal/domain/ordering/components/fulfillment-dropdown';
import { InputValueMap } from '@/utils/new-order';
import { Moment } from 'moment-timezone';
import { OrderEntryCheckoutSummaryEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-checkout-summary-empty-state';
import { OrderEntryCheckoutSummaryErrorState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-checkout-summary-error-state';
import { OrderSummaryAlerts } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/components/order-summary-alerts';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import {
  selectSalesPortalCartOrderId,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { SideDrawer, SideDrawerRef } from '@experiences/components/side-drawer';
import {
  TimeslotOption,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useFulfillmentSelection } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-fulfillment-selection';
import { useOrderEntryCommissionLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-order-entry-commission-loader';
import { useOrderSummaryMetricRows } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-order-summary-metric-rows';
import { useResetFulfillmentOnOrderSubmission } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-reset-fulfillment-on-order-submission';
import { useSalesPortalCheckoutAdditionalInputs } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-sales-portal-checkout-additional-inputs';
import { useSalesPortalOrderSummaryLoader } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/loaders/use-sales-portal-order-summary-loader';
import { useSalesPortalPoNumberDisplayMode } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-po-number-display-mode';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';
import { useSaveAndValidateSalesPortalCartMutator } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import { useSaveOrderFulfillmentSettings } from '@/experiences/sales-portal/domain/ordering/blocs/hooks/use-save-order-fulfillment-settings';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { useShallow } from 'zustand/react/shallow';
import { useSubmitOrderMutator } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/mutators/use-submit-order-mutator';
import { useSupplierDynamicConfigLoader } from '@/experiences/sales-portal/blocs/loaders/use-supplier-dynamic-config-loader';

const FOOTER_MIN_HEIGHT = 103;
const HEADER_SCROLL_THRESHOLD = 140; // Start animating after 100px scroll

interface CheckoutPanelProps {
  additionalOrderValidationRoles: EmployeeRole[];
  authenticatedUserUUID: string | null;
  availabilityConstraints: AvailabilityConstraints | null;
  drawerRef?: React.RefObject<SideDrawerRef | null>;
  employeeRole: EmployeeRole | null;
  fulfillment: {
    availableFulfillments: GetAvailableOrderFulfillmentResponse[];
    getTimeslotOptionsForDate: (date: Moment) => TimeslotOption[];
    onRefetch: () => void;
    savedSelectedFulfillment: SavedSelectedFulfillment | null;
    saveSelectedFulfillment: (fulfillment: SavedSelectedFulfillment) => void;
    selectableFulfillmentOptions: GetAvailableOrderFulfillmentResponse[];
    timezones: TimeZoneMap;
  };
  isCommissionsEnabled: boolean;
  isGrossProfitHidden: boolean;
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onOrderPlaced: () => void;
  restaurantUUID: string;
  selectedRestaurant: RestaurantListData;
  supplierUUID: string;
}

export const CheckoutPanel: React.FC<CheckoutPanelProps> = ({
  additionalOrderValidationRoles,
  authenticatedUserUUID,
  fulfillment: {
    availableFulfillments,
    selectableFulfillmentOptions,
    onRefetch,
    savedSelectedFulfillment,
    saveSelectedFulfillment,
    timezones,
  },
  availabilityConstraints,
  drawerRef,
  isGrossProfitHidden,
  selectedRestaurant,
  restaurantUUID,
  employeeRole,
  isCommissionsEnabled,
  supplierUUID,
  isOpen,
  onClose,
  onOpen,
  onOrderPlaced,
}) => {
  const [styles, theme] = useStyles();
  const scrollY = useRef(new Animated.Value(0)).current;
  const { showErrorToast } = useExperienceToasts();

  // Additional input state and configuration
  const [additionalInputValueMap, setAdditionalInputValueMap] =
    useState<InputValueMap>({});

  const { supplierDynamicConfig, loading: supplierDynamicConfigLoading } =
    useSupplierDynamicConfigLoader({
      supplierUUID,
    });
  const { resetFulfillmentOnOrderSubmission } =
    useResetFulfillmentOnOrderSubmission({
      availableFulfillments,
      chatUUID: selectedRestaurant.chatUUID,
      restaurantUUID,
      supplierUUID,
      timezones,
    });
  const supplierMetadata = useSalesPortalSupplierMetadataLoader({
    supplierUUID,
  });
  const {
    error: poNumberDisplayModeError,
    loading: poNumberDisplayModeLoading,
    poNumberDisplayMode,
    refetch: refetchPoNumberDisplayMode,
    refetching: refetchingPoNumberDisplayMode,
  } = useSalesPortalPoNumberDisplayMode({
    restaurantUUID,
    supplierUUID,
  });
  const showPoNumberDisplayModeError =
    !!poNumberDisplayModeError || refetchingPoNumberDisplayMode;
  const orderSummaryConfig = supplierDynamicConfig?.order_summary_config;
  const showOrderNotes = supplierMetadata.showOrderNotes;

  const {
    additionalInputErrorMap,
    additionalInputs,
    areAdditionalInputsValid,
    clearAdditionalInputError,
    clearAllAdditionalInputError,
    validateAdditionalInput,
  } = useSalesPortalCheckoutAdditionalInputs(
    orderSummaryConfig,
    poNumberDisplayMode,
    showOrderNotes,
    additionalInputValueMap,
  );

  const clampedScrollY = Animated.diffClamp(
    scrollY,
    0,
    HEADER_SCROLL_THRESHOLD,
  );

  const titleOpacity = clampedScrollY.interpolate({
    extrapolate: 'clamp',
    inputRange: [0, HEADER_SCROLL_THRESHOLD * 0.5, HEADER_SCROLL_THRESHOLD],
    outputRange: [0, 0.7, 1],
  });

  // Order validation and summary
  const orderUUID = useCartStore(useShallow(selectSalesPortalCartOrderId));

  // Commission: fetched independently so checkout always has fresh values
  const { commissionDisplayValue, refetchCommission } =
    useOrderEntryCommissionLoader({
      authenticatedUserUUID,
      currencyCode: null,
      enabled: isCommissionsEnabled,
      orderUUID,
    });

  const {
    orderValidation: firstOrderValidation,
    saveAndValidateCart,
    isSavingAndValidatingCart,
  } = useSaveAndValidateSalesPortalCartMutator();
  const hasValidatedOrder =
    !!orderUUID && !isSavingAndValidatingCart && !!firstOrderValidation;
  const canPlaceOrder = firstOrderValidation?.canPlaceOrder ?? false;

  const {
    data: savedSalesPortalSummaryData,
    error: savedSalesPortalSummaryError,
    loading: savedSalesPortalSummaryLoading,
    loadOrderSummary,
    reset: resetOrderSummary,
  } = useSalesPortalOrderSummaryLoader({ isGrossProfitHidden });

  const [summarizeOrderFailed, setSummarizeOrderFailed] = useState(false);

  const salesPortalSummaryLoading =
    !orderUUID ||
    savedSalesPortalSummaryLoading ||
    (!savedSalesPortalSummaryData &&
      !summarizeOrderFailed &&
      !savedSalesPortalSummaryError);

  const hasSummaryError =
    (!!savedSalesPortalSummaryError && !salesPortalSummaryLoading) ||
    summarizeOrderFailed;

  const firstSummary = savedSalesPortalSummaryData?.[0];
  const currency = firstSummary?.currency || '';

  // Build row data for each split order
  const splitOrderMetricRows = useOrderSummaryMetricRows(
    firstSummary?.splitOrderMetrics ?? [],
    currency,
    { isGrossProfitHidden },
  );

  const splitOrderCount = firstSummary?.splitOrderMetrics?.length ?? 0;
  const isSummaryEmpty = splitOrderCount === 0;
  const isLoading = !hasValidatedOrder || salesPortalSummaryLoading;
  const showFallbackState =
    !isLoading &&
    (hasSummaryError || showPoNumberDisplayModeError || isSummaryEmpty);
  const showSummaryContent = !showFallbackState;

  const summarizeOrder = useCallback(async () => {
    setSummarizeOrderFailed(false);
    resetOrderSummary();

    // shows toast if cart is not valid or save fails
    const success = await saveAndValidateCart();

    // After save, read the (potentially newly created) orderUUID from the store
    const resolvedOrderUUID =
      useCartStore.getState().getCart()?.orderUUID ?? null;

    if (success && resolvedOrderUUID) {
      loadOrderSummary({
        variables: {
          orderIds: [resolvedOrderUUID],
          restaurantUUID,
          supplierUUID,
        },
      });
      refetchCommission?.();
    } else {
      setSummarizeOrderFailed(true);
    }
  }, [
    loadOrderSummary,
    refetchCommission,
    resetOrderSummary,
    restaurantUUID,
    supplierUUID,
    saveAndValidateCart,
  ]);

  useEffect(() => {
    if (isOpen) {
      onOpen?.();
    } else {
      setIsOrderPlaced(false);
    }
  }, [isOpen, onOpen]);

  // validate cart when checkout panel is opened
  useEffect(() => {
    if (isOpen) {
      summarizeOrder();
    }
    // we intentionally exclude summarizeOrder from deps to avoid re-fetching when its dependencies change while the panel is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // fufillment
  const cutoffTimeString = availabilityConstraints?.cutoffTimeString;
  const orderMinimumDisplayString =
    availabilityConstraints?.orderMinimumDisplayString;

  const { saveFulfillment } = useSaveOrderFulfillmentSettings({
    saveSelectedFulfillment,
  });
  const handleSaveSelectedFulfillment = useCallback(
    (fulfillment: SavedSelectedFulfillment) => {
      resetOrderSummary();
      saveFulfillment(fulfillment, {
        onError: summarizeOrder,
        onSuccess: summarizeOrder,
      });
    },
    [resetOrderSummary, saveFulfillment, summarizeOrder],
  );

  const fulfillmentSelection = useFulfillmentSelection({
    additionalOrderValidationRoles,
    availableFulfillments,
    employeeRole,
    savedSelectedFulfillment,
    timezones,
  });

  // If there's an error loading the summary or the summary is empty this fallback will be shown
  const SummaryFallbackContent = useMemo(() => {
    if (hasSummaryError) {
      return (
        <View style={styles.errorContainer}>
          <OrderEntryCheckoutSummaryErrorState onPress={summarizeOrder} />
        </View>
      );
    }
    if (showPoNumberDisplayModeError) {
      return (
        <View style={styles.errorContainer}>
          <OrderEntryCheckoutSummaryErrorState
            description={i18n.t(
              'sales_portal.checkout_panel.po_setting.error.description',
            )}
            loading={refetchingPoNumberDisplayMode}
            onPress={() => refetchPoNumberDisplayMode()}
            title={i18n.t('sales_portal.checkout_panel.po_setting.error.title')}
          />
        </View>
      );
    }
    return (
      <View style={styles.errorContainer}>
        <OrderEntryCheckoutSummaryEmptyState onPress={onClose} />
      </View>
    );
  }, [
    showPoNumberDisplayModeError,
    hasSummaryError,
    onClose,
    refetchPoNumberDisplayMode,
    refetchingPoNumberDisplayMode,
    styles.errorContainer,
    summarizeOrder,
  ]);

  // ordering
  const handleAdditionalInputChange = (
    id: string,
    title: string,
    value: string,
  ) => {
    setAdditionalInputValueMap((previous) => ({
      ...previous,
      [id]: { title, value },
    }));
  };

  const areAdditionalInputsInvalid = !areAdditionalInputsValid;
  const { submitOrder, loading: isSubmittingOrder } = useSubmitOrderMutator();
  const [isOrderPlaced, setIsOrderPlaced] = useState(false);

  const isPlaceOrderDisabled =
    !hasValidatedOrder ||
    !canPlaceOrder ||
    isSubmittingOrder ||
    isLoading ||
    supplierDynamicConfigLoading ||
    poNumberDisplayModeLoading ||
    showPoNumberDisplayModeError ||
    areAdditionalInputsInvalid;

  const handleSubmitOrder = useCallback(async () => {
    if (!orderUUID) {
      return;
    }

    if (areAdditionalInputsInvalid) {
      return;
    }

    try {
      const result = await submitOrder(orderUUID, additionalInputValueMap);
      if (!result?.data?.submitOrder?.order_id) {
        onClose();
        showErrorToast(
          result?.errors?.[0]?.message ??
            i18n.t('sales_portal.order_entry.checkout_panel.place_order_error'),
        );
        return;
      }

      setAdditionalInputValueMap({});
      clearAllAdditionalInputError();
      // if we have an order id, order placement was successful
      useCartStore.getState().clearCart();
      resetFulfillmentOnOrderSubmission();
      // Show success state inline — avoids stacked RN Modal issues on native
      setIsOrderPlaced(true);
      onOrderPlaced();
    } catch (error) {
      onClose();
      const errorMessage =
        error instanceof Error
          ? error.message
          : i18n.t('sales_portal.order_entry.checkout_panel.place_order_error');
      showErrorToast(errorMessage);
    }
  }, [
    additionalInputValueMap,
    areAdditionalInputsInvalid,
    clearAllAdditionalInputError,
    onClose,
    onOrderPlaced,
    orderUUID,
    resetFulfillmentOnOrderSubmission,
    showErrorToast,
    submitOrder,
  ]);

  // Keyboard shortcut: Shift+Enter to place order
  const handlePlaceOrderShortcut = useCallback(() => {
    if (isPlaceOrderDisabled) {
      return;
    }
    handleSubmitOrder();
  }, [isPlaceOrderDisabled, handleSubmitOrder]);

  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.PlaceOrder,
    handlePlaceOrderShortcut,
    [handlePlaceOrderShortcut],
    {},
    isOpen,
    SalesPortalKeyboardScope.CHECKOUT_PANEL,
  );

  const titleElement = useMemo(() => {
    const titleAnimatedStyle = {
      opacity: titleOpacity,
    };

    return (
      <Animated.View style={[styles.titleContainer, titleAnimatedStyle]}>
        <View style={styles.title}>
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {selectedRestaurant.restaurantName}
          </Typography>
        </View>
      </Animated.View>
    );
  }, [
    selectedRestaurant.restaurantName,
    styles.title,
    styles.titleContainer,
    theme.sk.color.text.primary,
    titleOpacity,
  ]);

  const placeOrderButtonLabel =
    splitOrderCount <= 1
      ? i18n.t('sales_portal.order_entry.checkout_panel.place_order')
      : i18n.t('sales_portal.order_entry.checkout_panel.place_orders', {
          count: splitOrderCount,
        });

  if (isOrderPlaced) {
    return (
      <SideDrawer
        headerElement={titleElement}
        isOpen={isOpen}
        onClose={onClose}
        ref={drawerRef}
      >
        <CheckoutPanelOrderPlaced
          onClose={onClose}
          restaurantName={selectedRestaurant.restaurantName}
        />
      </SideDrawer>
    );
  }

  /** TODO (DSR-3483): Fix SideDrawer header blur  */
  return (
    <SideDrawer
      headerElement={titleElement}
      isOpen={isOpen}
      onClose={onClose}
      ref={drawerRef}
    >
      <View style={styles.container}>
        <View
          style={[
            styles.scrollWrapper,
            // On native iOS, flex:1 gives ScrollView 0 height; use absolute positioning instead
            Platform.OS !== 'web' ? styles.scrollWrapperNative : undefined,
          ]}
        >
          <ScrollView
            automaticallyAdjustKeyboardInsets={Platform.OS !== 'web'}
            contentContainerStyle={
              showFallbackState ? styles.scrollContentCentered : undefined
            }
            onScroll={Animated.event(
              [{ nativeEvent: { contentOffset: { y: scrollY } } }],
              {
                useNativeDriver: false, // Can't use native driver for height/scale transforms
              },
            )}
            scrollEventThrottle={20}
            style={styles.content}
          >
            <View style={styles.header}>
              <Typography
                color={theme.sk.color.text.primary}
                variant="heading-lg"
              >
                {selectedRestaurant.restaurantName}
              </Typography>
              <Typography
                color={theme.sk.color.text.tertiary}
                variant="paragraph-md"
              >
                {selectedRestaurant.restaurantAddress}
              </Typography>

              <View style={styles.fufillmentContainer}>
                {orderMinimumDisplayString ? (
                  <View style={styles.fufillmentConstraintContainer}>
                    <ArrowDownBottomLine fill={theme.sk.color.icon.tertiary} />
                    <Typography
                      color={theme.sk.color.text.tertiary}
                      variant="paragraph-md"
                    >
                      {`${i18n.t('sales_portal.fulfillment_settings.minimum')} ${orderMinimumDisplayString}`}
                    </Typography>
                  </View>
                ) : null}
                {cutoffTimeString ? (
                  <View style={styles.fufillmentConstraintContainer}>
                    <ClockIcon
                      height={16}
                      stroke={theme.sk.color.icon.tertiary}
                      width={16}
                    />
                    <Typography
                      color={theme.sk.color.text.tertiary}
                      variant="paragraph-md"
                    >
                      {`${i18n.t('sales_portal.fulfillment_settings.cutoff')} ${cutoffTimeString}`}
                    </Typography>
                  </View>
                ) : null}
              </View>
            </View>
            {showFallbackState ? (
              <>
                {hasSummaryError ? null : (
                  <OrderSummaryAlerts
                    orderValidation={firstOrderValidation ?? null}
                  />
                )}
                {SummaryFallbackContent}
              </>
            ) : (
              <CheckoutPanelContent
                additionalInputErrorMap={additionalInputErrorMap}
                additionalInputs={additionalInputs}
                additionalInputValueMap={additionalInputValueMap}
                firstOrderAction={
                  <FulfillmentDropdown
                    availableFulfillments={availableFulfillments}
                    createIsDateDisabled={
                      fulfillmentSelection.createIsDateDisabled
                    }
                    disabled={isLoading || isSubmittingOrder}
                    getTimeslotCategories={
                      fulfillmentSelection.getTimeslotCategories
                    }
                    onRefetch={onRefetch}
                    // The checkout panel will handle disabling and enabling the order entry table scopes
                    onSave={handleSaveSelectedFulfillment}
                    restaurantUUID={restaurantUUID}
                    savedSelectedFulfillment={savedSelectedFulfillment}
                    selectableFulfillmentOptions={selectableFulfillmentOptions}
                    timezones={timezones}
                    trigger={ChangeFulfillmentTrigger}
                    validateFulfillmentDate={
                      fulfillmentSelection.validateFulfillmentDate
                    }
                    validateTimeslot={fulfillmentSelection.validateTimeslot}
                  />
                }
                isLoading={isLoading}
                onAdditionalInputBlur={validateAdditionalInput}
                onAdditionalInputChange={handleAdditionalInputChange}
                onAdditionalInputClearError={clearAdditionalInputError}
                orderEntrySummary={firstSummary}
                orderValidation={firstOrderValidation ?? null}
                savedSelectedFulfillment={savedSelectedFulfillment}
                selectedFulfillmentDisplay={
                  fulfillmentSelection.selectedFulfillmentDisplay
                }
                splitOrderMetricRows={splitOrderMetricRows}
                timezones={timezones}
              />
            )}
          </ScrollView>
        </View>
        <View
          style={[
            styles.footer,
            Platform.OS === 'web' ? styles.footerWeb : styles.footerNative,
          ]}
        >
          <View style={styles.footerLeft}>
            {showSummaryContent ? (
              <CheckoutPanelTotalSummary
                commissionDisplayValue={
                  isCommissionsEnabled ? commissionDisplayValue : undefined
                }
                isLoading={isLoading}
                totalAmount={firstSummary?.orderFees.total?.amountDisplayString}
              />
            ) : null}
          </View>
          <View style={styles.footerRight}>
            {showSummaryContent ? (
              <Button
                accessibilityLabel={placeOrderButtonLabel}
                disabled={isPlaceOrderDisabled}
                loading={isSubmittingOrder}
                onPress={handleSubmitOrder}
                size={ButtonSize.Medium}
                variant={ButtonVariant.Primary}
                width={160}
              >
                {placeOrderButtonLabel}
              </Button>
            ) : null}
          </View>
        </View>
      </View>
      <View
        style={
          isLoading || showFallbackState
            ? styles.disclaimerHidden
            : styles.disclaimerShown
        }
      >
        <Typography color={theme.sk.color.text.tertiary} variant="paragraph-sm">
          {i18n.t('final_price_pending')}
        </Typography>
      </View>
    </SideDrawer>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    position: 'relative',
  },
  content: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
  },
  disclaimerHidden: {
    opacity: 0,
  },
  disclaimerShown: {
    opacity: 1,
  },
  errorContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  footer: {
    flexDirection: 'row',
    flexShrink: 0,
    justifyContent: 'space-between',
    minHeight: 40,
  },
  footerLeft: {
    flex: 1,
  },
  footerNative: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  footerRight: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.sk.spacing.spaceMedium,
  },
  footerWeb: {
    marginTop: theme.sk.spacing.spaceSMedium,
  },
  fufillmentConstraintContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  fufillmentContainer: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  header: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceXsmall,
    overflow: 'hidden',
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  scrollContentCentered: {
    flexGrow: 1,
  },
  scrollWrapper: {
    flex: 1,
    minHeight: 0,
  },
  /** On native iOS, flex:1 on ScrollView resolves to 0 height; use absolute positioning instead. */
  scrollWrapperNative: {
    bottom: FOOTER_MIN_HEIGHT,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  title: {
    maxWidth: 250,
  },
  titleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 24,
    justifyContent: 'center',
    margin: 'auto',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
}));
